import React, { useEffect, useMemo, useState } from "react";
import { adminListMedia } from "../../services/admin";

const KIND_LABELS = {
  image: "Imágenes",
  video: "Videos",
  audio: "Audios",
  file: "Archivos",
  embed: "Embeds",
};

const KIND_SINGULAR = {
  image: "imagen",
  video: "video",
  audio: "audio",
  file: "archivo",
  embed: "embed",
};

const ALL_KINDS = ["image", "video", "audio", "file", "embed"];

function normalizeMediaResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.media)) return data.media;
  if (Array.isArray(data?.assets)) return data.assets;
  return [];
}

function getMediaUrl(item = {}) {
  return (
    item.url ||
    item.public_url ||
    item.file_url ||
    item.asset_url ||
    item.r2_url ||
    item.embed_url ||
    item.external_url ||
    ""
  );
}

function getMediaTitle(item = {}) {
  return (
    item.title ||
    item.name ||
    item.filename ||
    item.original_name ||
    item.alt ||
    getMediaUrl(item) ||
    "Media"
  );
}

function getMediaDescription(item = {}) {
  return (
    item.description ||
    item.caption ||
    item.label ||
    item.tags ||
    item.kind ||
    item.mime_type ||
    item.content_type ||
    item.type ||
    ""
  );
}

function inferKind(item = {}) {
  const kind = String(item.kind || "").toLowerCase();
  const mime = String(item.mime_type || item.content_type || item.type || "").toLowerCase();
  const url = getMediaUrl(item).toLowerCase();

  if (["image", "video", "audio", "file", "embed"].includes(kind)) return kind;

  if (mime.startsWith("image/") || /\.(png|jpe?g|webp|gif|avif|svg)(\?.*)?$/.test(url)) {
    return "image";
  }

  if (mime.startsWith("video/") || /\.(mp4|webm|mov|m4v)(\?.*)?$/.test(url)) {
    return "video";
  }

  if (mime.startsWith("audio/") || /\.(mp3|wav|m4a|aac|ogg)(\?.*)?$/.test(url)) {
    return "audio";
  }

  if (
    mime.includes("html") ||
    url.includes("youtube.com") ||
    url.includes("youtu.be") ||
    url.includes("vimeo.com") ||
    String(item.embed_code || item.iframe || "").trim()
  ) {
    return "embed";
  }

  return "file";
}

function isAllowedKind(item = {}, allowedKinds = []) {
  if (!allowedKinds?.length) return true;
  return allowedKinds.includes(inferKind(item));
}

function getAllowedKinds(allowedKinds = []) {
  const clean = Array.isArray(allowedKinds)
    ? allowedKinds.map((kind) => String(kind || "").toLowerCase()).filter(Boolean)
    : [];

  return clean.length ? clean : ALL_KINDS;
}

function getKindIcon(kind) {
  if (kind === "video") return "▶";
  if (kind === "audio") return "♪";
  if (kind === "embed") return "<>";
  if (kind === "file") return "DOC";
  return "IMG";
}

function kindLabel(kind) {
  return KIND_LABELS[kind] || kind || "Media";
}

function singularLabel(kind) {
  return KIND_SINGULAR[kind] || "media";
}

export default function MediaPicker({
  value,
  onChange,
  allowedKinds = ["image"],
  label = "Imagen",
  helper = "Elige una imagen desde Media. La URL se insertará automáticamente.",
  manualInput = false,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [isCompact, setIsCompact] = useState(false);
  const [search, setSearch] = useState("");
  const allowed = useMemo(() => getAllowedKinds(allowedKinds), [allowedKinds]);
  const [kindFilter, setKindFilter] = useState("all");

  async function loadMedia() {
    try {
      setLoading(true);
      setError("");

      const data = await adminListMedia();
      setItems(normalizeMediaResponse(data));
    } catch (err) {
      console.error("[MEDIA_PICKER] cannot_load_media", err);
      setError("No se pudo cargar Media.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMedia();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia("(max-width: 640px)");

    const updateCompact = () => {
      setIsCompact(Boolean(mediaQuery.matches));
    };

    updateCompact();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateCompact);
      return () => mediaQuery.removeEventListener("change", updateCompact);
    }

    mediaQuery.addListener(updateCompact);
    return () => mediaQuery.removeListener(updateCompact);
  }, []);

  useEffect(() => {
    setKindFilter("all");
  }, [allowedKinds]);

  const mediaOptions = useMemo(() => {
    const term = search.trim().toLowerCase();

    return items.filter((item) => {
      const url = getMediaUrl(item);
      if (!url) return false;

      const kind = inferKind(item);
      if (!isAllowedKind(item, allowed)) return false;
      if (kindFilter !== "all" && kind !== kindFilter) return false;

      if (!term) return true;

      const haystack = [
        getMediaTitle(item),
        getMediaDescription(item),
        kind,
        item.slug,
        item.key,
        item.path,
        item.folder,
        url,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [items, allowed, kindFilter, search]);

  const selectedUrl = String(value || "").trim();
  const selectedKind = useMemo(() => {
    const selectedItem = items.find((item) => getMediaUrl(item) === selectedUrl);
    return selectedItem ? inferKind(selectedItem) : "image";
  }, [items, selectedUrl]);

  const primaryKind = allowed.length === 1 ? allowed[0] : "media";
  const actionLabel = allowed.length === 1
    ? `Elegir ${singularLabel(primaryKind)} desde Media`
    : "Elegir desde Media";

  const modalInstruction = allowed.length === 1
    ? `Selecciona una ${singularLabel(primaryKind)}. ZEDRO insertará la URL automáticamente.`
    : "Selecciona el tipo de media y el recurso. ZEDRO insertará la URL automáticamente.";

  return (
    <div className="admNlField">
      <label>{label}</label>

      {selectedUrl ? (
        <div style={{ ...styles.selectedBox, ...(isCompact ? styles.selectedBoxCompact : {}) }}>
          {selectedKind === "image" ? (
            <img src={selectedUrl} alt="" style={{ ...styles.selectedImage, ...(isCompact ? styles.selectedImageCompact : {}) }} />
          ) : (
            <div style={{ ...styles.selectedPlaceholder, ...(isCompact ? styles.selectedImageCompact : {}) }}>
              {getKindIcon(selectedKind)}
            </div>
          )}

          <div style={styles.selectedContent}>
            <div style={styles.selectedTitle}>Media seleccionada</div>
            <div style={styles.selectedUrl}>{selectedUrl}</div>

            <div style={styles.actions}>
              <button
                type="button"
                className="admNl-btn"
                onClick={() => setOpen(true)}
              >
                Cambiar
              </button>

              <button
                type="button"
                className="admNl-btn ghost"
                onClick={() => onChange?.("")}
              >
                Quitar
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="admNl-btn"
          onClick={() => setOpen(true)}
          style={{ justifySelf: "start" }}
        >
          {actionLabel}
        </button>
      )}

      {manualInput && (
        <input
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="O pega aquí una URL manualmente"
          style={styles.manualInput}
        />
      )}

      <small>{helper}</small>

      {open && (
        <div style={{ ...styles.overlay, ...(isCompact ? styles.overlayCompact : {}) }} role="dialog" aria-modal="true">
          <div style={{ ...styles.modal, ...(isCompact ? styles.modalCompact : {}) }}>
            <div style={{ ...styles.modalHeader, ...(isCompact ? styles.modalHeaderCompact : {}) }}>
              <div>
                <h3 style={{ ...styles.modalTitle, ...(isCompact ? styles.modalTitleCompact : {}) }}>Biblioteca de Media</h3>
                <p style={{ ...styles.modalText, ...(isCompact ? styles.modalTextCompact : {}) }}>{modalInstruction}</p>
              </div>

              <button
                type="button"
                className="admNl-btn ghost"
                onClick={() => setOpen(false)}
                style={isCompact ? styles.closeButtonCompact : undefined}
              >
                Cerrar
              </button>
            </div>

            <div style={{ ...styles.toolbar, ...(isCompact ? styles.toolbarCompact : {}) }}>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por nombre, etiqueta o tipo"
                style={{ ...styles.searchInput, ...(isCompact ? styles.controlCompact : {}) }}
              />

              <select
                value={kindFilter}
                onChange={(event) => setKindFilter(event.target.value)}
                style={{ ...styles.kindSelect, ...(isCompact ? styles.controlCompact : {}) }}
                disabled={allowed.length <= 1}
              >
                <option value="all">
                  {allowed.length <= 1 ? kindLabel(allowed[0]) : "Todos los tipos"}
                </option>

                {allowed.length > 1 && allowed.map((kind) => (
                  <option key={kind} value={kind}>
                    {kindLabel(kind)}
                  </option>
                ))}
              </select>

              <button
                type="button"
                className="admNl-btn ghost"
                onClick={loadMedia}
                disabled={loading}
                style={isCompact ? styles.refreshButtonCompact : undefined}
              >
                Actualizar
              </button>
            </div>

            {loading && <p style={{ ...styles.modalText, ...(isCompact ? styles.modalTextCompact : {}) }}>Cargando media…</p>}

            {error && (
              <p style={{ ...styles.modalText, ...(isCompact ? styles.modalTextCompact : {}), color: "#9b2c2c" }}>
                {error}
              </p>
            )}

            {!loading && !error && mediaOptions.length === 0 && (
              <div style={{ ...styles.empty, ...(isCompact ? styles.emptyCompact : {}) }}>
                No hay resultados para mostrar con estos filtros.
              </div>
            )}

            <div style={{ ...styles.grid, ...(isCompact ? styles.gridCompact : {}) }}>
              {mediaOptions.map((item) => {
                const url = getMediaUrl(item);
                const title = getMediaTitle(item);
                const kind = inferKind(item);

                return (
                  <button
                    key={item.id || url}
                    type="button"
                    style={{ ...styles.mediaCard, ...(isCompact ? styles.mediaCardCompact : {}) }}
                    onClick={() => {
                      onChange?.(url);
                      setOpen(false);
                    }}
                  >
                    {kind === "image" ? (
                      <img src={url} alt={title} style={{ ...styles.mediaImage, ...(isCompact ? styles.mediaImageCompact : {}) }} />
                    ) : (
                      <div style={{ ...styles.mediaPlaceholder, ...(isCompact ? styles.mediaImageCompact : {}) }}>
                        {getKindIcon(kind)}
                      </div>
                    )}

                    <span style={{ ...styles.mediaTitle, ...(isCompact ? styles.mediaTitleCompact : {}) }}>{title}</span>
                    <span style={{ ...styles.mediaKind, ...(isCompact ? styles.mediaKindCompact : {}) }}>{kindLabel(kind)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  selectedBox: {
    display: "grid",
    gridTemplateColumns: "120px 1fr",
    gap: 12,
    alignItems: "center",
    padding: 10,
    borderRadius: 16,
    border: "1px solid rgba(44,59,81,.12)",
    background: "#fff",
  },

  selectedBoxCompact: {
    gridTemplateColumns: "1fr",
  },

  selectedImage: {
    width: "100%",
    height: 82,
    objectFit: "cover",
    borderRadius: 12,
    display: "block",
  },

  selectedImageCompact: {
    height: 150,
  },

  selectedPlaceholder: {
    width: "100%",
    height: 82,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg, rgba(15,47,70,.09), rgba(212,176,98,.16))",
    color: "#0f2f46",
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: ".08em",
  },

  selectedContent: {
    minWidth: 0,
  },

  selectedTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "#0f2f46",
    marginBottom: 4,
  },

  selectedUrl: {
    fontSize: 11,
    color: "rgba(44,59,81,.54)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
  },

  manualInput: {
    width: "100%",
    padding: 10,
    borderRadius: 12,
    border: "1px solid rgba(44,59,81,.14)",
    boxSizing: "border-box",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 2000,
    background: "rgba(8,22,34,.48)",
    display: "grid",
    placeItems: "center",
    padding: 20,
    boxSizing: "border-box",
    overflow: "hidden",
  },

  overlayCompact: {
    padding: 10,
    alignItems: "center",
    justifyItems: "center",
  },

  modal: {
    width: "min(920px, 100%)",
    maxHeight: "82vh",
    overflowY: "auto",
    overflowX: "hidden",
    borderRadius: 24,
    background: "#f8fafc",
    border: "1px solid rgba(204,174,73,.22)",
    boxShadow: "0 30px 90px rgba(0,0,0,.24)",
    padding: 18,
    boxSizing: "border-box",
    WebkitOverflowScrolling: "touch",
  },

  modalCompact: {
    width: "calc(100vw - 24px)",
    maxWidth: "calc(100vw - 24px)",
    maxHeight: "78dvh",
    borderRadius: 22,
    padding: 12,
  },

  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 16,
    minWidth: 0,
  },

  modalHeaderCompact: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "start",
    gap: 10,
    marginBottom: 12,
  },

  modalTitle: {
    margin: 0,
    color: "#0f2f46",
    fontSize: 22,
  },

  modalTitleCompact: {
    fontSize: 20,
    lineHeight: 1.12,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  modalText: {
    margin: "6px 0 0",
    fontSize: 13,
    color: "rgba(44,59,81,.64)",
    lineHeight: 1.5,
  },

  modalTextCompact: {
    fontSize: 12.5,
    lineHeight: 1.35,
  },

  closeButtonCompact: {
    minHeight: 42,
    padding: "8px 12px",
    whiteSpace: "nowrap",
  },

  toolbar: {
    display: "grid",
    gridTemplateColumns: "minmax(180px, 1fr) 180px auto",
    gap: 10,
    marginBottom: 14,
    minWidth: 0,
  },

  toolbarCompact: {
    gridTemplateColumns: "1fr",
    gap: 8,
  },

  searchInput: {
    width: "100%",
    minHeight: 42,
    boxSizing: "border-box",
    padding: "9px 12px",
    borderRadius: 12,
    border: "1px solid rgba(44,59,81,.14)",
    background: "#fff",
    color: "#0f2f46",
    fontSize: 14,
  },

  kindSelect: {
    width: "100%",
    minHeight: 42,
    boxSizing: "border-box",
    padding: "9px 12px",
    borderRadius: 12,
    border: "1px solid rgba(44,59,81,.14)",
    background: "#fff",
    color: "#0f2f46",
    fontSize: 14,
  },

  controlCompact: {
    minHeight: 46,
    fontSize: 16,
  },

  refreshButtonCompact: {
    width: "100%",
    minHeight: 46,
    justifyContent: "center",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 12,
    minWidth: 0,
  },

  gridCompact: {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },

  mediaCard: {
    display: "grid",
    gap: 8,
    padding: 8,
    borderRadius: 16,
    border: "1px solid rgba(44,59,81,.12)",
    background: "#fff",
    cursor: "pointer",
    textAlign: "left",
    minWidth: 0,
    overflow: "hidden",
  },

  mediaCardCompact: {
    borderRadius: 14,
    padding: 7,
  },

  mediaImage: {
    width: "100%",
    aspectRatio: "4 / 3",
    objectFit: "cover",
    borderRadius: 12,
    display: "block",
  },

  mediaImageCompact: {
    borderRadius: 11,
  },

  mediaPlaceholder: {
    width: "100%",
    aspectRatio: "4 / 3",
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg, rgba(15,47,70,.09), rgba(212,176,98,.16))",
    color: "#0f2f46",
    fontSize: 16,
    fontWeight: 900,
    letterSpacing: ".08em",
  },

  mediaTitle: {
    fontSize: 12,
    color: "#0f2f46",
    fontWeight: 800,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },

  mediaTitleCompact: {
    fontSize: 11.5,
  },

  mediaKind: {
    width: "fit-content",
    padding: "2px 7px",
    borderRadius: 999,
    background: "#eef7ff",
    border: "1px solid #cfe6f7",
    color: "#0f2f46",
    fontSize: 10,
    fontWeight: 900,
  },

  mediaKindCompact: {
    fontSize: 9.5,
  },

  empty: {
    padding: 22,
    borderRadius: 16,
    background: "#fff",
    border: "1px dashed rgba(44,59,81,.22)",
    color: "rgba(44,59,81,.62)",
    textAlign: "center",
  },

  emptyCompact: {
    padding: 16,
    fontSize: 13,
  },
};
